import React, { useEffect, useState } from 'react';

/**
 * "You are looking at yesterday's app."
 *
 * A BDA leaves this tab open all day. When we deploy, their tab keeps running
 * the JavaScript it loaded that morning — the browser only picks up a new build
 * on a reload — and nothing on screen says so. That is not cosmetic: the email
 * body is built IN THE BROWSER, so a stale tab keeps sending mail in the old
 * format hours after the fix is live. The Outlook spacing fix went out at 21:05
 * and mail was still going out cramped at 21:39 from a tab loaded before it.
 *
 * So ask the server what the current build is and say something when it differs.
 *
 * How it knows: every build gives the main bundle a new content hash, and
 * `index.html` names it. The tab re-fetches index.html and compares the hash in
 * it with the script the tab is itself running. Different hash = a newer build
 * is live. Nothing is compared against a version we hard-code, so this cannot
 * itself go stale. (Not `asset-manifest.json`, which would be the tidier
 * source: vercel.json rewrites everything outside /static to index.html, so
 * asking for the manifest returns the page.)
 *
 * It never reloads by itself — that would throw away a half-written email.
 */
const CHECK_EVERY_MS = 5 * 60 * 1000;

// The main bundle THIS tab is running, e.g. "/static/js/main.314dca8e.js".
const runningBundle = () => {
  const script = [...document.querySelectorAll('script[src]')]
    .map(s => s.getAttribute('src') || '')
    .find(src => /\/static\/js\/main\.[a-z0-9]+\.js$/i.test(src));
  return script ? script.replace(/^https?:\/\/[^/]+/, '') : null;
};

const deployedBundle = async () => {
  // no-store, or the browser answers from the very cache we are trying to see past.
  const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'text/html' } });
  if (!res.ok) return null;
  const html = await res.text();
  const match = /\/static\/js\/main\.[a-z0-9]+\.js/i.exec(html);
  return match ? match[0] : null;
};

const NewVersionBanner = () => {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const mine = runningBundle();
    // Development, or a build shape we do not recognise: say nothing at all.
    if (!mine) return undefined;
    let cancelled = false;

    const check = async () => {
      try {
        const live = await deployedBundle();
        if (!cancelled && live && live !== mine) setStale(true);
      } catch (err) {
        // Offline, or the manifest is not served. Nothing to tell the user.
      }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    // Coming back to the tab is exactly when a deploy is most likely to have
    // happened without them.
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!stale) return null;

  return (
    <div className="sticky top-0 z-50 bg-amber-50 border-b border-amber-300 px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-sm text-amber-900">
      <span>
        <strong>A newer version of Sales Hub is live.</strong> This tab is still running the older
        one — emails sent from it use the older formatting.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload(true)}
        className="px-3 py-1 rounded bg-amber-600 text-white font-medium hover:bg-amber-700"
      >
        Reload now
      </button>
    </div>
  );
};

export default NewVersionBanner;
